import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Users,
  UserPlus,
  DollarSign,
  MessageSquare,
  AlertCircle,
} from 'lucide-react';
import { dashboardApi, adminApi, settingsApi } from '../../services/api';
import { AppUser } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { SystemHealthBanner } from '../../components/Dashboard/SystemHealthBanner';
import { MetricCard } from '../../components/Dashboard/MetricCard';
import { KnowledgeAssistantCard } from '../../components/Dashboard/KnowledgeAssistantCard';
import { ConversationMetricsCard } from '../../components/Dashboard/ConversationMetricsCard';
import { EmptyState } from '../../components/Dashboard/EmptyState';

const USD_TO_INR = 83.5;

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

const Dashboard: React.FC = () => {
  const { isAdmin, isTeamMember, user } = useAuth();
  const userId = user?._id ?? null;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [apiKeysConfigured, setApiKeysConfigured] = useState(true);
  const [uptime, setUptime] = useState(0);
  const [aggregate, setAggregate] = useState({
    totalUsers: 0,
    newUsersToday: 0,
    totalCost: 0,
    costToday: 0,
    totalRequests: 0,
    requestsToday: 0,
    totalConversations: 0,
    conversationsToday: 0,
  });
  const [eventSummaries, setEventSummaries] = useState<any[]>([]);
  const [clients, setClients] = useState<AppUser[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>('');

  useEffect(() => {
    if (isAdmin && clients.length === 0) {
      fetchClients();
    }
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAdmin && !isTeamMember) {
      settingsApi.getApiKeysStatus()
        .then(res => {
          const { hasOpenaiApiKey, hasGeminiApiKey } = res.data.data ?? {};
          setApiKeysConfigured(!!(hasOpenaiApiKey && hasGeminiApiKey));
        })
        .catch(() => {/* leave apiKeysConfigured as true on error */});
    }
  }, [isAdmin, isTeamMember]);

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const response = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      if (response.data.success && response.data.data) {
        setClients(response.data.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const params: { clientId?: string } = {};
      if (isAdmin && selectedClientId) {
        params.clientId = selectedClientId;
      } else if (!isAdmin && userId) {
        params.clientId = userId;
      }

      const response = await dashboardApi.getSummary(params);
      if (response.data.success && response.data.data) {
        const { health, aggregate: agg, events } = response.data.data;
        setUptime(health.uptime);
        setAggregate(agg);
        setEventSummaries(events);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedClientId, isAdmin, userId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const assistantConversations = eventSummaries.map((e) => ({
    name: e.name,
    todayConversations: e.conversationsToday,
    totalConversations: e.totalConversations,
  }));

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-8">
      {/* Page Title */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground mb-1">Dashboard Overview</h1>
        <p className="text-sm text-muted-foreground">
          Monitor your platform's key metrics and system health
        </p>
      </div>

      {/* Client Filter (admins only) */}
      {isAdmin && (
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-gray-700">Filter by Client:</span>
            <select
              value={selectedClientId}
              disabled={loadingClients}
              onChange={e => setSelectedClientId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-[300px]"
            >
              <option value="">All Clients</option>
              {clients.map(client => (
                <option key={client._id} value={client._id}>
                  {client.name} ({client.email})
                </option>
              ))}
            </select>
            {selectedClientId && (
              <span className="text-sm text-gray-500">
                Showing: {clients.find(c => c._id === selectedClientId)?.name || 'Selected Client'}
              </span>
            )}
          </div>
        </div>
      )}

      {/* System Health Banner */}
      <SystemHealthBanner
        status="operational"
        message="All Systems Operational"
        details={`System uptime: ${formatUptime(uptime)} | All core services are running normally`}
        onRefresh={fetchDashboardData}
      />

      {/* API Keys Warning Banner (clients only, when keys not configured) */}
      {!isAdmin && !isTeamMember && !apiKeysConfigured && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
          <p className="flex-1 text-sm text-red-800">
            Knowledge Assistant(s) currently would be unavailable to the users because setup is incomplete.
            Go to Settings, provide the API keys for Gemini and OpenAI and enable the Knowledge Assistant(s).
          </p>
          <button
            onClick={() => navigate('/admin/settings')}
            className="flex-shrink-0 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
          >
            Go to Settings
          </button>
        </div>
      )}

      {/* Knowledge Assistants Section */}
      <section>
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Knowledge Assistants
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Overview of your knowledge assistant instances
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <KnowledgeAssistantCard
                key={i}
                name=""
                status="active"
                totalUsers={0}
                newUsersToday={0}
                costToday={0}
                totalCost={0}
                loading
              />
            ))}
          </div>
        ) : eventSummaries.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {eventSummaries.map((ev) => (
              <KnowledgeAssistantCard
                key={ev.id}
                name={ev.name}
                status={ev.status}
                totalUsers={ev.totalUsers}
                newUsersToday={ev.newUsersToday}
                costToday={ev.costToday}
                totalCost={ev.totalCost}
                thumbsUpPercentage={ev.thumbsUpPercentage}
                thumbsDownPercentage={ev.thumbsDownPercentage}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Calendar}
            title="No knowledge assistants found"
            description="Create your first knowledge assistant to start engaging with users"
          />
        )}
      </section>

      {/* Users Section */}
      <section>
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Users className="w-5 h-5 text-green-600" />
            Users
          </h2>
          <p className="text-sm text-muted-foreground mt-1">User engagement and growth metrics</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            title="Total Users (all assistants)"
            value={aggregate.totalUsers}
            icon={Users}
            color="green"
            loading={loading}
          />
          <MetricCard
            title="New Users Today (all assistants)"
            value={aggregate.newUsersToday}
            icon={UserPlus}
            badge={{ label: 'today', variant: 'info' }}
            color="purple"
            loading={loading}
          />
          <MetricCard
            title="Active Conversations Today"
            value={aggregate.conversationsToday}
            icon={Users}
            badge={{ label: 'active today', variant: 'success' }}
            color="blue"
            loading={loading}
          />
        </div>
      </section>

      {/* Cost Analytics Section */}
      <section>
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-orange-600" />
            Cost Analytics
          </h2>
          <p className="text-sm text-muted-foreground mt-1">AI usage costs and spending overview</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <MetricCard
            title="Total AI Cost (till date — all assistants)"
            value={`₹${(aggregate.totalCost * USD_TO_INR).toFixed(2)}`}
            secondaryValue={`$${aggregate.totalCost.toFixed(4)}`}
            icon={DollarSign}
            subtitle={`${aggregate.totalRequests} requests`}
            color="orange"
            loading={loading}
          />
          <MetricCard
            title="Total AI Cost (Today — all assistants)"
            value={`₹${(aggregate.costToday * USD_TO_INR).toFixed(2)}`}
            secondaryValue={`$${aggregate.costToday.toFixed(4)}`}
            icon={DollarSign}
            subtitle={`${aggregate.requestsToday} requests`}
            badge={{ label: 'today', variant: 'info' }}
            color="blue"
            loading={loading}
          />
        </div>
      </section>

      {/* Conversations Section */}
      <section>
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            Conversations
          </h2>
          <p className="text-sm text-muted-foreground mt-1">AI conversation activity and engagement</p>
        </div>
        <ConversationMetricsCard
          assistants={assistantConversations}
          loading={loading}
        />
      </section>
    </div>
  );
};

export default Dashboard;
